import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  Catalog,
  Cart,
  View,
  OfferItem,
  OrderMetaData,
  ErrorView,
  OrderStatus,
  ValidateStoreLocation,
  OrderEmail,
  CheckoutData,
  CookieData,
  ZoneData,
  CurrentCatData,
  StoreRules,
  OrderMetaState,
  SocialAuth,
  CustomerDetailsUpdate,
  CustomerInfoData,
  CombinedOrderState,
  StandalonePaymentOrderState,
  StandalonePaymentState,
  LinkCode,
  StoredCustomerStateData,
  MobileStoreData,
  StoreRecents,
  CustomerLoyaltyState,
  CustomerLoyaltyStateData,
  CustomerFooterHeightData,
  CustomerProfileStateData,
  CustomerProfileState,
  CustomerLoyaltyTransactionsState,
  CustomerLoyaltyTransactionsStateData,
  CustomerOrdersStateData,
  CustomerOrdersState,
} from './stores.reducer';
import { ClientStore, Offer } from 'src/app/stores/stores';
import { CustomerProfile } from '../types/CustomerSocialLogin';
import SlotItemCountUtils, {
  SlotCountIndex,
  SlotItemCapacity,
} from '../utils/SlotItemCountUtils';

export const getCatalogState = createFeatureSelector<Catalog>(
  'selectedStoreCatalog',
);
export const getSelectedStore = createSelector(
  getCatalogState,
  (state: Catalog) =>
    state && state?.selectedStore ? state?.selectedStore.data : null,
);
export const getSelectedStoreStatus = createSelector(
  getCatalogState,
  (state: Catalog) =>
    state && state.selectedStore ? state.selectedStore.status : null,
);
export const getSelectedStoreLoadingFrequency = createSelector(
  getCatalogState,
  (state: Catalog) =>
    state && state.selectedStore ? state.selectedStore.firstLoading : null,
);
export const getSelectedStoreCatalog = createSelector(
  getCatalogState,
  (state: Catalog) => (state && state.cataloglist ? state.cataloglist : null),
);
export const getIsCatalogLoading = createSelector(
  getCatalogState,
  (state: Catalog) => state?.cataloglist?.status === 'LOADING',
);
export const getSelectedStoreCategories = createSelector(
  getCatalogState,
  (state: Catalog) =>
    state &&
    state.cataloglist &&
    state.cataloglist.data &&
    state.cataloglist.data.categories
      ? state.cataloglist.data.categories
      : null,
);
export const getSelectedCategory = createSelector(
  getCatalogState,
  (state: Catalog) =>
    state && state.cataloglist && state.cataloglist.selectedCategory
      ? state.cataloglist.selectedCategory
      : null,
);
export const getLoadedCatalogLanguage = createSelector(
  getCatalogState,
  (state: Catalog) =>
    state && state.cataloglist && state.cataloglist.loadedLang
      ? state.cataloglist.loadedLang
      : null,
);
export const getSelectedStoreOpeningHours = createSelector(
  getSelectedStore,
  (store: ClientStore) => {
    if (store && store.specialSchedules) {
      return store.specialSchedules.find((s) => s.type === 'OPENING_HOURS');
    }
    return null;
  },
);

export const getSelectedStoreTimeZone = createSelector(
  getSelectedStore,
  (store: ClientStore) => {
    if (store) {
      return store.timeZone;
    }
    return null;
  },
);
export const getAvailableCatalogLanguages = createSelector(
  getCatalogState,
  (state: Catalog) =>
    state && state.availableCatalogLanguages
      ? state.availableCatalogLanguages
      : null,
);
export const getSelectedLang = createSelector(
  getCatalogState,
  (state: Catalog) => {
    if (state && state.cataloglist && state.cataloglist.selectedLang) {
      return state.cataloglist.selectedLang;
    } else {
      return null;
    }
  },
);

export const getOfferItem =
  createFeatureSelector<OfferItem>('currentOfferItem');
export const getCurrentOfferItem = createSelector(
  getOfferItem,
  (state: OfferItem) => state.offerItemState.data,
);
export const getCurrentOfferItemStatus = createSelector(
  getOfferItem,
  (state: OfferItem) => state.offerItemState.status,
);

/**
 * "Count against slot count" of every offer the customer UI has loaded: the
 * menu with its price variants, plus the open item's options and combo items.
 */
export const getSlotCountIndex = createSelector(
  getSelectedStoreCatalog,
  getCurrentOfferItem,
  (catalog, offerItem: Offer): SlotCountIndex =>
    SlotItemCountUtils.buildIndex([
      ...(offerItem ? [offerItem] : []),
      ...(catalog?.data?.offers ?? []),
      ...[
        ...(catalog?.data?.categories ?? []),
        ...(catalog?.data?.topLevelCategories ?? []),
      ].reduce<Offer[]>(
        (offers, category) => offers.concat(category?.offers ?? []),
        [],
      ),
    ]),
);

export const getCartState = createFeatureSelector<Cart>('currentCartState');
export const getCurrentCartStatus = createSelector(
  getCartState,
  (state: Cart | undefined | null) => state?.cartState?.status ?? null,
);

export const getCurrentCartState = createSelector(
  getCartState,
  (state: Cart | undefined | null) => state?.cartState?.data ?? null,
);

export const getCurrentCartContent = createSelector(
  getCartState,
  (state: Cart | undefined | null) => state?.cartState?.data?.orderItems ?? [],
);

export const getCurrentCartUuid = createSelector(
  getCartState,
  (state: Cart | undefined | null) => state?.cartState?.data?.uuid ?? null,
);

export const getCurrentPaymentTransaction = createSelector(
  getCartState,
  (state: Cart | undefined | null) =>
    state?.cartState?.data?.paymentTransaction ?? null,
);
export const getOrderStatus = createFeatureSelector<OrderStatus>('orderStatus');
export const getCurrentOrderStatus = createSelector(
  getOrderStatus,
  (state: OrderStatus) => state.orderStatus,
);
export const getCombinedOrder =
  createFeatureSelector<CombinedOrderState>('combinedOrderState');
export const getCombinedOrdersData = createSelector(
  getCombinedOrder,
  (state: CombinedOrderState) => state?.combinedOrderState,
);

export const getCreatedStandaloneOrder =
  createFeatureSelector<StandalonePaymentState>('standalonePaymentState');
export const getCreatedStandaloneOrderData = createSelector(
  getCreatedStandaloneOrder,
  (state: StandalonePaymentState) => state?.standalonePaymentOrderState,
);

export const getOrderMetaState = createFeatureSelector<OrderMetaState>(
  'currentOrderMetaState',
);
export const getCurrentOrderMetaState = createSelector(
  getOrderMetaState,
  (state: OrderMetaState) => state,
);
export const getOrderConsumptionMode = createSelector(
  getCurrentOrderMetaState,
  (orderMeta) => orderMeta?.data?.CONSUMPTION_MODE,
);
export const getCurrentOrderDeliveryMethod = createSelector(
  getOrderMetaState,
  (state: OrderMetaState) => state.data.deliveryMethod,
);
export const getCurrentOrderWishTime = createSelector(
  getOrderMetaState,
  (state: OrderMetaState) => state.data.wishTime,
);
export const getStoreOpeningInfo = createSelector(
  getOrderMetaState,
  (state: OrderMetaState) => state.storeOpeningInfo,
);
export const getSlots = createSelector(
  getOrderMetaState,
  (state: OrderMetaState) => state.storeOpeningInfo.slots,
);
export const getStoreOpenInDate = createSelector(
  getOrderMetaState,
  (state: OrderMetaState) => state.storeOpeningInfo.openInDate,
);
export const getOrderWish = createSelector(
  getOrderMetaState,
  (state: OrderMetaState) => state.wish,
);
export const getMultidayDetails = createSelector(
  getOrderMetaState,
  (state: OrderMetaState) => state.multidayDetails,
);
export const getDisplayMultidayDetails = createSelector(
  getOrderMetaState,
  (state: OrderMetaState) => state.multidayDetails?.displayMultidayDetails,
);
export const getMultidaySlotMap = createSelector(
  getOrderMetaState,
  (state: OrderMetaState) => state.multidayDetails?.slotMap,
);
export const getMultidaySelectedSlots = createSelector(
  getOrderMetaState,
  (state: OrderMetaState) => state.multidayDetails?.selectedSlots,
);
export const getMultidaySelectedAddresses = createSelector(
  getOrderMetaState,
  (state: OrderMetaState) => state.multidayDetails?.selectedAddresses,
);

/**
 * The "Maximum items per slot" capacity of the chosen wish time (#9840), or
 * null when the basket is not checked against a slot.
 */
export const getSelectedSlotItemCapacity = createSelector(
  getSelectedStore,
  getOrderMetaState,
  (store: ClientStore, state: OrderMetaState): SlotItemCapacity | null =>
    state.data?.wishTime
      ? SlotItemCountUtils.capacityOf(
          store?.settings,
          state.storeOpeningInfo?.slots?.selectedSlot,
        )
      : null,
);

export const getStoreViewState = createFeatureSelector<View>(
  'currentStoreViewState',
);
export const getCurrentStoreViewStatus = createSelector(
  getStoreViewState,
  (state: View) => state?.viewState?.status,
);
export const getUserLanguage = createSelector(
  getStoreViewState,
  (state: View) => state.viewState.ulang,
);
// Story #10478 - the pending shared item deep-link reference (or null).
export const getPendingSharedItem = createSelector(
  getStoreViewState,
  (state: View) => state?.viewState?.pendingSharedItem ?? null,
);

export const getErrorState = createFeatureSelector<ErrorView>('errorState');
export const getCurrentError = createSelector(
  getErrorState,
  (state: ErrorView) => (state && state.errorState ? state.errorState : null),
);

export const getStoreLocations = createFeatureSelector<ValidateStoreLocation>(
  'validateStoreLocation',
);
export const getStoreLocationsState = createSelector(
  getStoreLocations,
  (state: ValidateStoreLocation) =>
    state && state.storeLocationsState
      ? state.storeLocationsState.locationState
      : null,
);

export const getOrderEmailStatus =
  createFeatureSelector<OrderEmail>('orderEmailStatus');
export const getOrderEmailBody = createSelector(
  getOrderEmailStatus,
  (state: OrderEmail) =>
    state && state.orderEmail.emailState.body
      ? state.orderEmail.emailState.body
      : null,
);

export const getCheckoutState =
  createFeatureSelector<CheckoutData>('checkoutState');
export const getCookieState = createFeatureSelector<CookieData>('cookieState');
export const getCookieStatus = createSelector(
  getCookieState,
  (state: CookieData) => state.cookieState.status,
);

export const getZoneState = createFeatureSelector<ZoneData>('zoneState');

export const getSelectedCategoryState =
  createFeatureSelector<CurrentCatData>('currentCatState');
export const getSelectedTopLevelCategory = createSelector(
  getSelectedCategoryState,
  (state: CurrentCatData) =>
    state?.currentCategoryState?.selectedTopLevelCategory,
);

export const getStoreRules =
  createFeatureSelector<StoreRules>('storeRulesState');

export const getSocialAuth =
  createFeatureSelector<SocialAuth>('socialLoginState');

export const getSocialAuthStatus = createSelector(
  getSocialAuth,
  (state: SocialAuth) => state.socialLoginState.socialAuthStatus,
);

export const getCustomerDetailsUpdate =
  createFeatureSelector<CustomerDetailsUpdate>('customerDetailsUpdateState');

export const getTokens = createSelector(
  getSocialAuth,
  (state: SocialAuth) => state.socialLoginState.tokens,
);

/**
 * The signed-in customer's id while the storefront holds their token, or
 * `null` (card #11561). Keyed on the JWT because that is what
 * `CustomerTokenInterceptor` attaches, so a customer-authenticated request may
 * only be sent while this is non-null; `userId` alone is no test, since a
 * signed-out slice holds the truthy sentinel `-1`.
 */
export const getAuthenticatedCustomerId = createSelector(
  getSocialAuth,
  (state: SocialAuth): number | null =>
    state?.socialLoginState?.tokens?.jwt ? state.socialLoginState.userId : null,
);

export const getCustomerLoyaltyStateData =
  createFeatureSelector<CustomerLoyaltyStateData>('customerLoyaltyState');

export const getCustomerLoyaltyState = createSelector(
  getCustomerLoyaltyStateData,
  (state: CustomerLoyaltyStateData) => state.customerLoyaltyState,
);

export const getCustomerLoyaltyTransactionsStateData =
  createFeatureSelector<CustomerLoyaltyTransactionsStateData>(
    'customerLoyaltyTransactionsState',
  );

export const getCustomerLoyaltyTransactionsState = createSelector(
  getCustomerLoyaltyTransactionsStateData,
  (state: CustomerLoyaltyTransactionsStateData) =>
    state.customerLoyaltyTransactionsState,
);

export const getCustomerProfileStateData =
  createFeatureSelector<CustomerProfileStateData>('customerProfileState');

export const getCustomerProfileState = createSelector(
  getCustomerProfileStateData,
  (state: CustomerProfileStateData) => state?.customerProfileState,
);

export const getCustomerProfile = createSelector(
  getCustomerProfileState,
  (state: CustomerProfileState) => state.data,
);

export const getCustomerProfileAddresses = createSelector(
  getCustomerProfile,
  (customerProfile: CustomerProfile) => customerProfile?.addresses || [],
);

export const getCustomerProfileStatus = createSelector(
  getCustomerProfileState,
  (state: CustomerProfileState) => state.status,
);

export const getCustomerProfileUpdateStatus = createSelector(
  getCustomerProfileState,
  (state: CustomerProfileState) => state?.updateStatus,
);

export const getCustomerProfileOnlyUpdateStatus = createSelector(
  getCustomerProfileState,
  (state: CustomerProfileState) => state.profileUpdateStatus,
);

export const getCustomerAddressUpdateStatus = createSelector(
  getCustomerProfileState,
  (state: CustomerProfileState) => state.addressUpdateStatus,
);

export const getSelectedStoreCustomerInfoState =
  createFeatureSelector<CustomerInfoData>('customerInfo');
export const getCustomerInfo = createSelector(
  getSelectedStoreCustomerInfoState,
  (state: CustomerInfoData) => state?.customerInfo,
);

export const getLinkCode = createFeatureSelector<LinkCode>('linkCodeState');
export const getLinkCodeStatus = createSelector(
  getLinkCode,
  (state: LinkCode) => state.linkCodeState.status,
);

export const getStoredCustomerData =
  createFeatureSelector<StoredCustomerStateData>('storedCustomerState');
export const getStoredCustomerDataStatus = createSelector(
  getStoredCustomerData,
  (state: StoredCustomerStateData) => state.storedCustomerState.status,
);

export const getMobileStoreState =
  createFeatureSelector<MobileStoreData>('mobileStoreState');
export const getMobileStoreAlias = createSelector(
  getMobileStoreState,
  (state: MobileStoreData) => state.mobileStoreState.storeAlias,
);

export const getCustomerFooterHeightState =
  createFeatureSelector<CustomerFooterHeightData>('customerFooterHeightState');
export const getCustomerFooterHeight = createSelector(
  getCustomerFooterHeightState,
  (state: CustomerFooterHeightData) =>
    state.customerFooterHeightState.customerFooterHeight,
);

export const getStoreRecents =
  createFeatureSelector<StoreRecents>('storeRecents');
export const getStoreRecentsList = createSelector(
  getStoreRecents,
  (state: StoreRecents) => state.storeRecents.storeRecentsList,
);

export const getCustomerOrdersStateData =
  createFeatureSelector<CustomerOrdersStateData>('customerOrdersState');

export const getCustomerOrdersState = createSelector(
  getCustomerOrdersStateData,
  (state: CustomerOrdersStateData) => state.customerOrdersState,
);
